import React, { useState, useCallback } from 'react';
import { 
  Download, 
  FileText, 
  PieChart, 
  Check, 
  X 
} from 'lucide-react';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

// Interface for Report Configuration
interface ReportConfiguration {
  type: string;
  availableFields: {
    category: string;
    fields: string[];
  }[];
}

// Report Configuration Definitions
const REPORT_CONFIGURATIONS: ReportConfiguration[] = [
  {
    type: 'transactions',
    availableFields: [
      {
        category: 'Transaction Details',
        fields: [
          'Transaction ID', 
          'Amount', 
          'Date', 
          'Type', 
          'Status'
        ]
      },
      {
        category: 'User Information',
        fields: [
          'User ID', 
          'Username', 
          'Email'
        ]
      },
      {
        category: 'Financial Metrics',
        fields: [
          'Currency', 
          'Exchange Rate', 
          'Fees'
        ]
      }
    ]
  },
  {
    type: 'users',
    availableFields: [
      {
        category: 'Personal Information',
        fields: [
          'User ID', 
          'Full Name', 
          'Email', 
          'Registration Date'
        ]
      },
      {
        category: 'Activity Metrics',
        fields: [
          'Login Count', 
          'Total Transactions', 
          'Last Active Date', 
          'Account Status'
        ]
      },
      {
        category: 'Profile Details',
        fields: [
          'Account Type', 
          'Verification Status', 
          'Country', 
          'Language'
        ]
      }
    ]
  },
  {
    type: 'financial',
    availableFields: [
      {
        category: 'Revenue Tracking',
        fields: [
          'Total Revenue', 
          'Net Profit', 
          'Gross Margin', 
          'Revenue by Category'
        ]
      },
      {
        category: 'Expense Management',
        fields: [
          'Total Expenses', 
          'Operational Costs', 
          'Marketing Expenses', 
          'Overhead'
        ]
      },
      {
        category: 'Financial Health',
        fields: [
          'Cash Flow', 
          'Profitability Ratio', 
          'Debt Ratio', 
          'Liquidity Metrics'
        ]
      }
    ]
  },
  {
    type: 'risk',
    availableFields: [
      {
        category: 'Risk Assessment',
        fields: [
          'Risk Level', 
          'Risk Category', 
          'Probability', 
          'Impact Score'
        ]
      },
      {
        category: 'Transaction Risk',
        fields: [
          'Suspicious Transactions', 
          'Fraud Indicators', 
          'Anomaly Detection', 
          'Risk Trigger Points'
        ]
      },
      {
        category: 'Compliance',
        fields: [
          'Regulatory Compliance', 
          'AML Indicators', 
          'KYC Status', 
          'Audit Trail'
        ]
      }
    ]
  }
];

const CustomReportGeneration: React.FC = () => {
  // State Management
  const [reportType, setReportType] = useState<string>('');
  const [selectedFields, setSelectedFields] = useState<{[key: string]: string[]}>({});
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Find configuration for current report type
  const currentConfig = REPORT_CONFIGURATIONS.find(
    config => config.type === reportType
  );

  // Toggle field selection
  const toggleFieldSelection = useCallback((category: string, field: string) => {
    setSelectedFields(prev => {
      const currentCategoryFields = prev[category] || [];
      const updatedCategoryFields = currentCategoryFields.includes(field)
        ? currentCategoryFields.filter(f => f !== field)
        : [...currentCategoryFields, field];

      return {
        ...prev,
        [category]: updatedCategoryFields
      };
    });
  }, []);

  // Check if a field is selected
  const isFieldSelected = useCallback((category: string, field: string) => {
    return (selectedFields[category] || []).includes(field);
  }, [selectedFields]);

  // Generate Report (Mock implementation)
  const generateReport = () => {
    // In a real-world scenario, you'd fetch actual data based on selected fields
    const mockData = [
      { 
        'Transaction ID': 'TRX001', 
        'Amount': 1000, 
        'Date': '2024-02-15',
        'Type': 'Income',
        'Status': 'Completed'
      }
    ];

    // Convert to worksheet
    const worksheet = XLSX.utils.json_to_sheet(mockData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    // Generate buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    
    // Save file
    const fileName = `${reportType}_report_${new Date().toISOString()}.xlsx`;
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
    FileSaver.saveAs(blob, fileName);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <PieChart className="mr-2 h-5 w-5 text-purple-500" /> 
          Custom Report Generation
        </h2>
        <button 
          onClick={generateReport}
          disabled={!reportType || Object.keys(selectedFields).length === 0}
          className="text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="mr-2 h-4 w-4" /> Generate Report
        </button>
      </div>
      
      <div className="space-y-4">
        {/* Report Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Report Type
          </label>
          <select
            value={reportType}
            onChange={(e) => {
              setReportType(e.target.value);
              setSelectedFields({}); // Reset selected fields
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Report Type</option>
            {REPORT_CONFIGURATIONS.map(config => (
              <option key={config.type} value={config.type}>
                {config.type.charAt(0).toUpperCase() + config.type.slice(1)} Report
              </option>
            ))}
          </select>
        </div>

        {/* Field Selection */}
        {currentConfig && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Fields to Include
            </label>
            <div className="space-y-4">
              {currentConfig.availableFields.map((category) => (
                <div key={category.category} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-md font-semibold text-gray-900 mb-3">
                    {category.category}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {category.fields.map((field) => (
                      <div 
                        key={field} 
                        className="flex items-center"
                      >
                        <input
                          type="checkbox"
                          id={`${category.category}-${field}`}
                          checked={isFieldSelected(category.category, field)}
                          onChange={() => toggleFieldSelection(category.category, field)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label 
                          htmlFor={`${category.category}-${field}`} 
                          className="ml-2 block text-sm text-gray-900"
                        >
                          {field}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date Range Selection */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({
                ...prev, 
                startDate: e.target.value
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({
                ...prev, 
                endDate: e.target.value
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Selected Fields Summary */}
        {Object.keys(selectedFields).length > 0 && (
          <div className="mt-4 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-md font-semibold text-gray-900 mb-3">
              Selected Fields
            </h3>
            {Object.entries(selectedFields).map(([category, fields]) => (
              <div key={category} className="mb-2">
                <p className="text-sm font-medium text-gray-700">{category}:</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {fields.map(field => (
                    <span 
                      key={field} 
                      className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                    >
                      {field}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomReportGeneration;